import React, { useState, useRef, useCallback } from "react";
import { useApi, apiPost, apiDelete } from "../hooks/useApi.ts";
import { SearchSelect } from "./SearchSelect.tsx";

/** Parse estimate string like "1m", "1h", "1.5h", "90m" into minutes */
function parseEstimate(input: string): number | null {
  const trimmed = input.trim().toLowerCase();
  const match = trimmed.match(/^(\d+\.?\d*)\s*(m|h)$/);
  if (!match) {
    const n = parseFloat(trimmed);
    return isNaN(n) ? null : Math.round(n);
  }
  const value = parseFloat(match[1]!);
  if (isNaN(value)) return null;
  return match[2] === "h" ? Math.round(value * 60) : Math.round(value);
}

/** Format minutes into a friendly string like "1m" or "1.5h" */
function formatEstimate(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const hours = minutes / 60;
  if (hours === Math.floor(hours)) return `${hours}h`;
  return `${parseFloat(hours.toFixed(1))}h`;
}

function EstimateInput({ value, onChange, style }: {
  value: number;
  onChange: (minutes: number) => void;
  style?: React.CSSProperties;
}) {
  const [text, setText] = useState(() => formatEstimate(value));
  const lastExternal = useRef(value);

  // Sync from parent when the external value changes
  if (value !== lastExternal.current) {
    lastExternal.current = value;
    setText(formatEstimate(value));
  }

  const commit = (input: string) => {
    const parsed = parseEstimate(input);
    if (parsed !== null && parsed > 0) {
      onChange(parsed);
      setText(formatEstimate(parsed));
      lastExternal.current = parsed;
    } else {
      setText(formatEstimate(value));
    }
  };

  const step = (direction: 1 | -1) => {
    const isHours = text.trim().toLowerCase().endsWith("h");
    let next: number;
    if (isHours) {
      next = value + direction * 30; // 0.5h = 30m
    } else {
      next = value + direction * 5;  // 5m
    }
    if (next < 1) next = 1;
    onChange(next);
    setText(formatEstimate(next));
    lastExternal.current = next;
  };

  return (
    <div style={{ display: "inline-flex", alignItems: "stretch", ...style }}>
      <input
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => commit(text)}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit(text);
          if (e.key === "ArrowUp") { e.preventDefault(); step(1); }
          if (e.key === "ArrowDown") { e.preventDefault(); step(-1); }
        }}
        style={{ width: "60px", textAlign: "center", borderRadius: "var(--radius) 0 0 var(--radius)", borderRight: "none" }}
      />
      <div style={{ display: "flex", flexDirection: "column", border: "1px solid var(--color-border)", borderRadius: "0 var(--radius) var(--radius) 0" }}>
        <button
          type="button"
          onClick={() => step(1)}
          style={{ flex: 1, padding: "0 6px", fontSize: "10px", lineHeight: 1, background: "var(--color-bg)", border: "none", borderBottom: "1px solid var(--color-border)", cursor: "pointer" }}
        >&#9650;</button>
        <button
          type="button"
          onClick={() => step(-1)}
          style={{ flex: 1, padding: "0 6px", fontSize: "10px", lineHeight: 1, background: "var(--color-bg)", border: "none", cursor: "pointer" }}
        >&#9660;</button>
      </div>
    </div>
  );
}

interface TaskGroup {
  id: number;
  name: string;
  description: string;
  status: string;
  source_type: string;
  project_id: number;
  parent_task_id: number | null;
  ai_output: string;
  created_at: string;
  subtask_count: number;
  total_tasks: number;
  created_tasks: number;
}

interface TaskPlan {
  parentTask: {
    name: string;
    description: string;
    assigneeId: number | null;
    startDate: string;
    dueDate: string;
    estimateMinutes: number;
  };
  subtasks: Array<{
    type: string;
    name: string;
    assigneeId: number | null;
    startDate: string;
    dueDate: string;
    estimateMinutes: number;
  }>;
}

interface PreviewResponse {
  plans: TaskPlan[];
  createdTaskMap: Record<string, number>;
  pendingTaskIds: number[];
}

interface Tasklist {
  id: number;
  name: string;
}

export function TaskGroups() {
  const { data: taskGroups, loading, refetch } = useApi<TaskGroup[]>("/api/task-groups");
  const { data: configData } = useApi<{ active: { teamwork: { baseUrl: string } } }>("/api/config");
  const twBaseUrl = configData?.active.teamwork.baseUrl?.replace(/\/$/, "") || "";
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [preview, setPreview] = useState<TaskPlan[] | null>(null);
  const [createdTaskMap, setCreatedTaskMap] = useState<Record<string, number>>({});
  const [tasklists, setTasklists] = useState<Tasklist[]>([]);
  const [globalTasklistId, setGlobalTasklistId] = useState<number>(0);
  const [tasklistOverrides, setTasklistOverrides] = useState<Record<number, number>>({});
  const [creatingIdx, setCreatingIdx] = useState<number | null>(null);
  const [createdIdxs, setCreatedIdxs] = useState<Set<number>>(new Set());
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [pendingInFlight, setPendingInFlight] = useState<Set<number>>(new Set());
  const [pendingTasks, setPendingTasks] = useState<Set<number>>(new Set());
  const pendingQueueRef = useRef<Array<{ twTaskId: number; action: "mark" | "unmark" }>>([]);
  const pendingProcessingRef = useRef(false);

  const processPendingQueue = useCallback(async () => {
    if (pendingProcessingRef.current) return;
    pendingProcessingRef.current = true;

    while (pendingQueueRef.current.length > 0) {
      const item = pendingQueueRef.current.shift()!;
      try {
        if (item.action === "mark") {
          await apiPost(`/api/tasks/${item.twTaskId}/mark-pending`, {});
          setPendingTasks((prev) => new Set(prev).add(item.twTaskId));
        } else {
          await apiPost(`/api/tasks/${item.twTaskId}/unmark-pending`, {});
          setPendingTasks((prev) => {
            const next = new Set(prev);
            next.delete(item.twTaskId);
            return next;
          });
        }
      } catch (err) {
        alert(`Failed on task ${item.twTaskId}: ${(err as Error).message}`);
      }
      setPendingInFlight((prev) => {
        const next = new Set(prev);
        next.delete(item.twTaskId);
        return next;
      });
    }

    pendingProcessingRef.current = false;
  }, []);
  const [minimizedIdxs, setMinimizedIdxs] = useState<Set<number>>(new Set());
  const [manualTwId, setManualTwId] = useState<Record<number, string>>({});
  const [selectedGroupStatus, setSelectedGroupStatus] = useState<string>("");

  const handlePreview = async (group: TaskGroup) => {
    setSelectedId(group.id);
    setSelectedProjectId(group.project_id);
    setPreview(null);
    setCreatedTaskMap({});
    setGlobalTasklistId(0);
    setTasklistOverrides({});
    setCreatedIdxs(new Set());
    setEditingIdx(null);
    setPendingTasks(new Set());
    setManualTwId({});
    setSelectedGroupStatus(group.status);
    try {
      const [data, lists] = await Promise.all([
        apiPost<PreviewResponse>(`/api/task-groups/${group.id}/preview`, {}),
        fetch(`/api/projects/${group.project_id}/tasklists`).then(r => r.json()) as Promise<Tasklist[]>,
      ]);
      setPreview(data.plans);
      setCreatedTaskMap(data.createdTaskMap);
      setPendingTasks(new Set(data.pendingTaskIds || []));
      setTasklists(lists);
      // Auto-minimize created tasks
      const autoMinimized = new Set<number>();
      data.plans.forEach((p: TaskPlan, i: number) => {
        if (data.createdTaskMap[p.parentTask.name]) autoMinimized.add(i);
      });
      setMinimizedIdxs(autoMinimized);
    } catch (err) {
      alert((err as Error).message);
    }
  };

  const updatePlan = (planIdx: number, updated: TaskPlan) => {
    setPreview((prev) => prev?.map((p, i) => (i === planIdx ? updated : p)) || null);
  };

  const removeSubtask = (planIdx: number, subIdx: number) => {
    setPreview((prev) =>
      prev?.map((p, i) => {
        if (i !== planIdx) return p;

        const remaining = p.subtasks.filter((_, j) => j !== subIdx);
        if (remaining.length === 0) {
          return { ...p, subtasks: remaining };
        }

        // Close gaps: each subtask starts the next business day after the previous one's due
        const adjusted = [...remaining];
        for (let j = 0; j < adjusted.length; j++) {
          const sub = adjusted[j]!;
          const subStart = toDate(sub.startDate);
          const subDue = toDate(sub.dueDate);
          const durationDays = Math.round((subDue.getTime() - subStart.getTime()) / (1000 * 60 * 60 * 24));

          if (j === 0) {
            // First subtask: keep its start, or pull to parent start if removed subtask was first
            if (subIdx === 0) {
              const newStart = nextBusinessDay(toDate(p.parentTask.startDate));
              const newDue = new Date(newStart);
              newDue.setDate(newDue.getDate() + durationDays);
              adjusted[j] = { ...sub, startDate: fmtDate(newStart), dueDate: fmtDate(newDue) };
            }
          } else {
            const prevDue = toDate(adjusted[j - 1]!.dueDate);
            if (subStart > prevDue) {
              // Gap exists — close it
              const pushTo = new Date(prevDue);
              pushTo.setDate(pushTo.getDate() + 1);
              const newStart = nextBusinessDay(pushTo);
              const newDue = new Date(newStart);
              newDue.setDate(newDue.getDate() + durationDays);
              adjusted[j] = { ...sub, startDate: fmtDate(newStart), dueDate: fmtDate(newDue) };
            }
          }
        }

        // Update parent dates
        const firstStart = adjusted[0]!.startDate;
        const lastDue = adjusted[adjusted.length - 1]!.dueDate;

        return {
          ...p,
          parentTask: {
            ...p.parentTask,
            startDate: firstStart < p.parentTask.startDate ? firstStart : firstStart,
            dueDate: lastDue,
          },
          subtasks: adjusted,
        };
      }) || null
    );
  };

  const removePlan = (planIdx: number) => {
    setPreview((prev) => prev?.filter((_, i) => i !== planIdx) || null);
  };

  const toDate = (s: string) => new Date(s + "T00:00:00");
  const fmtDate = (d: Date) => d.toISOString().split("T")[0]!;

  const shiftDate = (dateStr: string, days: number): string => {
    const d = toDate(dateStr);
    d.setDate(d.getDate() + days);
    return fmtDate(d);
  };

  /** Advance to next business day (skip weekends) */
  const nextBusinessDay = (d: Date): Date => {
    const result = new Date(d);
    while (result.getDay() === 0 || result.getDay() === 6) {
      result.setDate(result.getDate() + 1);
    }
    return result;
  };

  const handleStartDateChange = (planIdx: number, plan: TaskPlan, newStartDate: string) => {
    const oldStart = toDate(plan.parentTask.startDate);
    const newStart = toDate(newStartDate);
    const diffDays = Math.round((newStart.getTime() - oldStart.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return;

    updatePlan(planIdx, {
      ...plan,
      parentTask: {
        ...plan.parentTask,
        startDate: newStartDate,
        dueDate: shiftDate(plan.parentTask.dueDate, diffDays),
      },
      subtasks: plan.subtasks.map((sub) => ({
        ...sub,
        startDate: shiftDate(sub.startDate, diffDays),
        dueDate: shiftDate(sub.dueDate, diffDays),
      })),
    });
  };

  /** When a subtask due date is pushed out, cascade to all subsequent subtasks and update parent */
  const handleSubtaskDueDateChange = (planIdx: number, plan: TaskPlan, subIdx: number, newDueDate: string) => {
    const updated = [...plan.subtasks];
    updated[subIdx] = { ...updated[subIdx]!, dueDate: newDueDate };

    const newDue = toDate(newDueDate);

    // Cascade: for each subsequent subtask, if its start is before the new due, push it forward
    for (let j = subIdx + 1; j < updated.length; j++) {
      const sub = updated[j]!;
      const subStart = toDate(sub.startDate);
      const subDue = toDate(sub.dueDate);

      if (subStart <= newDue) {
        // Duration in calendar days for this subtask
        const durationDays = Math.round((subDue.getTime() - subStart.getTime()) / (1000 * 60 * 60 * 24));

        // New start = next business day after the previous subtask's due
        const prevDue = toDate(updated[j - 1]!.dueDate);
        const pushTo = new Date(prevDue);
        pushTo.setDate(pushTo.getDate() + 1);
        const newSubStart = nextBusinessDay(pushTo);

        const newSubDue = new Date(newSubStart);
        newSubDue.setDate(newSubDue.getDate() + durationDays);

        updated[j] = {
          ...sub,
          startDate: fmtDate(newSubStart),
          dueDate: fmtDate(newSubDue),
        };
      } else {
        break; // no further cascading needed
      }
    }

    // Parent due date = last subtask's due date
    const lastDue = updated[updated.length - 1]!.dueDate;

    updatePlan(planIdx, {
      ...plan,
      parentTask: {
        ...plan.parentTask,
        dueDate: lastDue,
      },
      subtasks: updated,
    });
  };

  /** When the parent due date changes, proportionally rescale all subtasks to fit */
  const handleParentDueDateChange = (planIdx: number, plan: TaskPlan, newDueDate: string) => {
    if (plan.subtasks.length === 0) {
      updatePlan(planIdx, {
        ...plan,
        parentTask: { ...plan.parentTask, dueDate: newDueDate },
      });
      return;
    }

    const oldStart = toDate(plan.parentTask.startDate);
    const oldEnd = toDate(plan.parentTask.dueDate);
    const newEnd = toDate(newDueDate);
    const oldSpan = oldEnd.getTime() - oldStart.getTime();
    const newSpan = newEnd.getTime() - oldStart.getTime();

    if (oldSpan <= 0 || newSpan <= 0) {
      updatePlan(planIdx, {
        ...plan,
        parentTask: { ...plan.parentTask, dueDate: newDueDate },
      });
      return;
    }

    const ratio = newSpan / oldSpan;

    const updated = plan.subtasks.map((sub) => {
      const subStartOffset = toDate(sub.startDate).getTime() - oldStart.getTime();
      const subEndOffset = toDate(sub.dueDate).getTime() - oldStart.getTime();

      const scaledStart = new Date(oldStart.getTime() + Math.round(subStartOffset * ratio));
      const scaledEnd = new Date(oldStart.getTime() + Math.round(subEndOffset * ratio));

      return {
        ...sub,
        startDate: fmtDate(nextBusinessDay(scaledStart)),
        dueDate: fmtDate(scaledEnd),
      };
    });

    updatePlan(planIdx, {
      ...plan,
      parentTask: { ...plan.parentTask, dueDate: newDueDate },
      subtasks: updated,
    });
  };

  /** When a subtask start date changes, adjust its due date to preserve duration and cascade subsequent subtasks */
  const handleSubtaskStartDateChange = (planIdx: number, plan: TaskPlan, subIdx: number, newStartDate: string) => {
    const sub = plan.subtasks[subIdx]!;
    const oldStart = toDate(sub.startDate);
    const oldDue = toDate(sub.dueDate);
    const durationDays = Math.round((oldDue.getTime() - oldStart.getTime()) / (1000 * 60 * 60 * 24));

    const newStart = toDate(newStartDate);
    const newDue = new Date(newStart);
    newDue.setDate(newDue.getDate() + durationDays);

    const updated = [...plan.subtasks];
    updated[subIdx] = { ...sub, startDate: newStartDate, dueDate: fmtDate(newDue) };

    // Cascade subsequent subtasks if they now overlap
    for (let j = subIdx + 1; j < updated.length; j++) {
      const next = updated[j]!;
      const nextStart = toDate(next.startDate);
      const nextDue = toDate(next.dueDate);
      const prevDue = toDate(updated[j - 1]!.dueDate);

      if (nextStart <= prevDue) {
        const nextDuration = Math.round((nextDue.getTime() - nextStart.getTime()) / (1000 * 60 * 60 * 24));
        const pushTo = new Date(prevDue);
        pushTo.setDate(pushTo.getDate() + 1);
        const newNextStart = nextBusinessDay(pushTo);
        const newNextDue = new Date(newNextStart);
        newNextDue.setDate(newNextDue.getDate() + nextDuration);

        updated[j] = {
          ...next,
          startDate: fmtDate(newNextStart),
          dueDate: fmtDate(newNextDue),
        };
      } else {
        break;
      }
    }

    // Update parent dates to encompass all subtasks
    const firstStart = updated[0]!.startDate;
    const lastDue = updated[updated.length - 1]!.dueDate;

    updatePlan(planIdx, {
      ...plan,
      parentTask: {
        ...plan.parentTask,
        startDate: firstStart < plan.parentTask.startDate ? firstStart : plan.parentTask.startDate,
        dueDate: lastDue > plan.parentTask.dueDate ? lastDue : lastDue,
      },
      subtasks: updated,
    });
  };

  const getTasklistForPlan = (planIdx: number) =>
    tasklistOverrides[planIdx] || globalTasklistId;

  const handleCreate = async (id: number, plan: TaskPlan, planIdx: number) => {
    const resolvedTasklistId = getTasklistForPlan(planIdx);
    if (!resolvedTasklistId) {
      alert("Please select a tasklist for this task");
      return;
    }
    setCreatingIdx(planIdx);
    try {
      const result = await apiPost<{ parentTaskId: number; subtaskIds: number[] }>(
        `/api/task-groups/${id}/create`,
        { tasklistId: resolvedTasklistId, plan }
      );
      setCreatedIdxs((prev) => new Set(prev).add(planIdx));
      // Track the new parent task ID so Mark Pending works immediately
      setCreatedTaskMap((prev) => ({ ...prev, [plan.parentTask.name]: result.parentTaskId }));
      // Auto-minimize the newly created task
      setMinimizedIdxs((prev) => new Set(prev).add(planIdx));
      refetch();
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setCreatingIdx(null);
    }
  };

  const handleMarkPending = (twTaskId: number) => {
    setPendingInFlight((prev) => new Set(prev).add(twTaskId));
    pendingQueueRef.current.push({ twTaskId, action: "mark" });
    processPendingQueue();
  };

  const handleUnmarkPending = (twTaskId: number) => {
    setPendingInFlight((prev) => new Set(prev).add(twTaskId));
    pendingQueueRef.current.push({ twTaskId, action: "unmark" });
    processPendingQueue();
  };

  const handleDelete = async (group: TaskGroup) => {
    if (!confirm(`Delete task group "${group.name}"?`)) return;
    try {
      await apiDelete(`/api/task-groups/${group.id}`);
      if (selectedId === group.id) {
        setSelectedId(null);
        setPreview(null);
      }
      refetch();
    } catch (err) {
      alert((err as Error).message);
    }
  };

  /** Get the Teamwork task ID for a plan, if it's been created */
  const getTwTaskId = (plan: TaskPlan): number | null => {
    return createdTaskMap[plan.parentTask.name] || null;
  };

  const handleLinkTwId = async (planIdx: number, plan: TaskPlan) => {
    const twId = Number(manualTwId[planIdx]);
    if (!twId || !selectedId) return;
    try {
      await apiPost(`/api/task-groups/${selectedId}/link-task`, {
        taskName: plan.parentTask.name,
        twTaskId: twId,
      });
      setCreatedTaskMap((prev) => ({ ...prev, [plan.parentTask.name]: twId }));
      setManualTwId((prev) => ({ ...prev, [planIdx]: "" }));
    } catch (err) {
      alert((err as Error).message);
    }
  };

  const isGroupCreatedOrPartial = selectedGroupStatus === "created" || selectedGroupStatus === "partial";

  if (loading) return <div className="loading">Loading task groups...</div>;

  return (
    <div>
      <div className="page-header">
        <h1>Task Groups</h1>
        <p>Review and create tasks in Teamwork</p>
      </div>

      <div className="card">
        {taskGroups && taskGroups.length > 0 ? (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Source</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {taskGroups.map((group) => (
                <tr key={group.id}>
                  <td>{group.name}</td>
                  <td><span className="badge badge-info">{group.source_type}</span></td>
                  <td>
                    <span className={`badge ${
                      group.status === "created" ? "badge-success" :
                      group.status === "partial" ? "badge-warning" :
                      group.status === "pending_review" ? "badge-warning" :
                      "badge-neutral"
                    }`}>
                      {group.status}
                    </span>
                  </td>
                  <td>
                    {group.created_tasks} / {group.total_tasks}
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => handlePreview(group)}
                      >
                        {group.status === "created" || group.status === "partial" ? "View / Manage" : "Preview & Create"}
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(group)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty-state">No task groups yet.</div>
        )}
      </div>

      {preview && selectedId && (
        <div className="card">
          <div className="card-header" style={{ marginBottom: "16px" }}>
            <div>
              <h2 style={{ margin: 0 }}>Task Preview</h2>
              {preview && (() => {
                const createdCount = preview.filter((p) => getTwTaskId(p) !== null).length;
                const totalCount = preview.length;
                return (
                  <span style={{ color: "var(--color-text-muted)", fontSize: "13px" }}>
                    {createdCount} of {totalCount} task{totalCount !== 1 ? "s" : ""} created in Teamwork
                  </span>
                );
              })()}
            </div>
            <div style={{ width: "260px" }}>
              <SearchSelect
                options={tasklists.map((tl) => ({ value: tl.id, label: tl.name }))}
                value={globalTasklistId}
                onChange={setGlobalTasklistId}
                placeholder="Default Tasklist..."
              />
            </div>
          </div>

          <div className="task-preview-list">
            {preview.map((plan, planIdx) => {
              const resolved = getTasklistForPlan(planIdx);
              const twTaskId = getTwTaskId(plan);
              const isAlreadyCreated = twTaskId !== null;
              const isEditing = editingIdx === planIdx;
              const isPending = twTaskId !== null && pendingTasks.has(twTaskId);
              const isMinimized = isAlreadyCreated && minimizedIdxs.has(planIdx);
              return (
                <div key={planIdx} className="task-card" style={isMinimized ? { paddingBottom: "8px" } : undefined}>
                  {/* Header */}
                  <div
                    className="task-card-header"
                    style={isAlreadyCreated ? { cursor: "pointer" } : undefined}
                    onClick={isAlreadyCreated ? () => setMinimizedIdxs((prev) => {
                      const next = new Set(prev);
                      next.has(planIdx) ? next.delete(planIdx) : next.add(planIdx);
                      return next;
                    }) : undefined}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {isEditing ? (
                        <input
                          type="text"
                          value={plan.parentTask.name}
                          onChange={(e) =>
                            updatePlan(planIdx, {
                              ...plan,
                              parentTask: { ...plan.parentTask, name: e.target.value },
                            })
                          }
                          className="task-edit-name"
                        />
                      ) : isAlreadyCreated && twBaseUrl ? (
                        <a
                          href={`${twBaseUrl}/#/tasks/${twTaskId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="task-card-title"
                          style={{ color: "var(--color-primary)", textDecoration: "none" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {plan.parentTask.name} &#8599;
                        </a>
                      ) : (
                        <div className="task-card-title">{plan.parentTask.name}</div>
                      )}
                      {!isEditing && (
                        <div className="task-card-meta">
                          {plan.parentTask.startDate} &rarr; {plan.parentTask.dueDate}
                          &nbsp;&middot;&nbsp;
                          {formatEstimate(plan.parentTask.estimateMinutes)} est.
                          &nbsp;&middot;&nbsp;
                          {plan.subtasks.length} subtask{plan.subtasks.length !== 1 ? "s" : ""}
                        </div>
                      )}
                    </div>
                    <div className="task-card-actions" onClick={(e) => e.stopPropagation()}>
                      {/* Status badges */}
                      {isAlreadyCreated && (
                        <span style={{ fontSize: "12px", color: "var(--color-text-muted)", userSelect: "none" }}>
                          {isMinimized ? "▸" : "▾"}
                        </span>
                      )}
                      {isAlreadyCreated && !isPending && (
                        <span className="badge badge-success">Created</span>
                      )}
                      {isAlreadyCreated && isPending && (
                        <span className="badge badge-info">Pending</span>
                      )}
                      {!isAlreadyCreated && isGroupCreatedOrPartial && (
                        <span className="badge badge-neutral">Not linked</span>
                      )}

                      {/* Edit button — only for uncreated tasks */}
                      {!isAlreadyCreated && (
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => setEditingIdx(isEditing ? null : planIdx)}
                        >
                          {isEditing ? "Done" : "Edit"}
                        </button>
                      )}

                      {/* Pending controls */}
                      {isAlreadyCreated && !isPending && (
                        <button
                          className="btn btn-sm btn-warning"
                          onClick={() => handleMarkPending(twTaskId!)}
                          disabled={pendingInFlight.has(twTaskId!)}
                        >
                          {pendingInFlight.has(twTaskId!) ? "Queued..." : "Mark Pending"}
                        </button>
                      )}
                      {isAlreadyCreated && isPending && (
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleUnmarkPending(twTaskId!)}
                          disabled={pendingInFlight.has(twTaskId!)}
                        >
                          {pendingInFlight.has(twTaskId!) ? "Queued..." : "Remove Pending"}
                        </button>
                      )}

                      {/* Link TW ID for unlinked tasks in created groups */}
                      {!isAlreadyCreated && isGroupCreatedOrPartial && !isEditing && (
                        <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                          <input
                            type="number"
                            placeholder="Teamwork Task ID"
                            value={manualTwId[planIdx] || ""}
                            onChange={(e) => setManualTwId((prev) => ({ ...prev, [planIdx]: e.target.value }))}
                            style={{ width: "140px", padding: "4px 8px", fontSize: "13px" }}
                            className="form-input"
                          />
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleLinkTwId(planIdx, plan)}
                            disabled={!manualTwId[planIdx]}
                          >
                            Link
                          </button>
                        </div>
                      )}

                      {/* Remove — only for uncreated tasks */}
                      {!isAlreadyCreated && (
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => removePlan(planIdx)}
                          title="Remove task"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>

                  {isMinimized ? null : <>
                  {/* Description */}
                  {isEditing ? (
                    <textarea
                      value={plan.parentTask.description}
                      onChange={(e) =>
                        updatePlan(planIdx, {
                          ...plan,
                          parentTask: { ...plan.parentTask, description: e.target.value },
                        })
                      }
                      rows={2}
                      className="task-edit-desc"
                    />
                  ) : (
                    plan.parentTask.description && (
                      <p className="task-card-desc">{plan.parentTask.description}</p>
                    )
                  )}

                  {/* Edit fields for dates/estimate */}
                  {isEditing && (
                    <div className="task-edit-fields">
                      <label>
                        Start (shifts all dates)
                        <input
                          type="date"
                          value={plan.parentTask.startDate}
                          onChange={(e) => handleStartDateChange(planIdx, plan, e.target.value)}
                        />
                      </label>
                      <label>
                        Due (rescales subtasks)
                        <input
                          type="date"
                          value={plan.parentTask.dueDate}
                          onChange={(e) => handleParentDueDateChange(planIdx, plan, e.target.value)}
                        />
                      </label>
                      <label>
                        Estimate
                        <EstimateInput
                          value={plan.parentTask.estimateMinutes}
                          onChange={(mins) =>
                            updatePlan(planIdx, {
                              ...plan,
                              parentTask: { ...plan.parentTask, estimateMinutes: mins },
                            })
                          }
                        />
                      </label>
                    </div>
                  )}

                  {/* Subtasks */}
                  <div className="subtask-list">
                    {plan.subtasks.map((sub, i) => (
                      <div key={i} className="subtask-row">
                        <span className="badge badge-neutral dept-badge">{sub.type}</span>
                        {isEditing ? (
                          <>
                            <input
                              type="text"
                              value={sub.name}
                              onChange={(e) => {
                                const updated = [...plan.subtasks];
                                updated[i] = { ...sub, name: e.target.value };
                                updatePlan(planIdx, { ...plan, subtasks: updated });
                              }}
                              className="subtask-edit-name"
                            />
                            <input
                              type="date"
                              value={sub.startDate}
                              onChange={(e) => handleSubtaskStartDateChange(planIdx, plan, i, e.target.value)}
                              className="subtask-edit-date"
                            />
                            <input
                              type="date"
                              value={sub.dueDate}
                              onChange={(e) => handleSubtaskDueDateChange(planIdx, plan, i, e.target.value)}
                              className="subtask-edit-date"
                            />
                            <EstimateInput
                              value={sub.estimateMinutes}
                              onChange={(mins) => {
                                const updated = [...plan.subtasks];
                                updated[i] = { ...sub, estimateMinutes: mins };
                                updatePlan(planIdx, { ...plan, subtasks: updated });
                              }}
                            />
                            <button
                              className="subtask-remove"
                              onClick={() => removeSubtask(planIdx, i)}
                              title="Remove subtask"
                            >
                              &times;
                            </button>
                          </>
                        ) : (
                          <>
                            <span className="subtask-name">{sub.name}</span>
                            <span className="subtask-dates">
                              {sub.startDate} &rarr; {sub.dueDate}
                            </span>
                            <span className="subtask-hours">
                              {formatEstimate(sub.estimateMinutes)}
                            </span>
                          </>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Footer: tasklist + create (only for uncreated tasks) */}
                  {!isAlreadyCreated && (
                    <div className="task-card-footer">
                      <SearchSelect
                        options={tasklists.map((tl) => ({ value: tl.id, label: tl.name }))}
                        value={tasklistOverrides[planIdx] || 0}
                        onChange={(val) =>
                          setTasklistOverrides((prev) => ({ ...prev, [planIdx]: val }))
                        }
                        placeholder={
                          globalTasklistId
                            ? `Use default (${tasklists.find((t) => t.id === globalTasklistId)?.name || ""})`
                            : "Override tasklist..."
                        }
                        style={{ width: "240px" }}
                      />
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => handleCreate(selectedId, plan, planIdx)}
                        disabled={creatingIdx === planIdx || !resolved}
                      >
                        {creatingIdx === planIdx ? "Creating..." : "Create in Teamwork"}
                      </button>
                    </div>
                  )}
                  </>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
